import { jsonStringifySafe } from "@repo/utils";
import {
  deriveVmFamiliesFromAssetIds,
  type VmFamily,
} from "@repo/utils/chain-derivation";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import hash from "object-hash";
import invariant from "tiny-invariant";
// strategy-utils.ts
import z from "zod";
import {
  AVAILABLE_CONDITIONAL_FUNCTION_IDS,
  type ConditionalFunctionId,
  type CondLeftArgsNoRuntime,
  type CondRightArgsNoRuntime,
  type CurrentMarketCapFnArgs,
  type CurrentPriceFnArgs,
  type SimpleMovingAverageArgs,
} from "./strategy-conditionals";
import type {
  AcceptedStrategyBlocks,
  AssetIdFormat,
  EthOrSolanaAssetIdFormat,
  InputDataForSolver,
  Strategy,
} from "./strategy-types";
import {
  isAssetIdsFormat,
  isConditionalStrategyBlock,
  isTokenAssetStrategyBlock,
} from "./strategy-types";

// Serialization and Deserialization
export const serializeStrategy = (strategy: Strategy): string => {
  // Convert undefined to null for proper serialization
  const replacer = (key: string, value: any) => {
    // Special handling for count field - undefined means 0
    if (key === "count" && value === undefined) {
      return 0;
    }
    return value;
  };
  return jsonStringifySafe(strategy, replacer);
};

export const deserializeStrategy = (serializedStrategy: string): Strategy => {
  return JSON.parse(serializedStrategy) as Strategy;
};

// JANK
type HasAssetArg =
  | CurrentPriceFnArgs
  | CurrentMarketCapFnArgs
  | SimpleMovingAverageArgs;

interface AssetMinimalData {
  assetId: EthOrSolanaAssetIdFormat;
  defiTokens?: Array<{
    assetId: EthOrSolanaAssetIdFormat;
  }>;
}

interface AssetsForStrategy {
  assets: Array<AssetMinimalData>;
}

export function getAllAssetIdsInvolvedInStrategy(
  block: AcceptedStrategyBlocks,
): {
  assets: Array<AssetMinimalData>;
  baseAssets: Array<EthOrSolanaAssetIdFormat>;
  defiAssets: Array<EthOrSolanaAssetIdFormat>;
} {
  // const assetIds: AssetIdFormat[] = [];

  const assets: AssetMinimalData[] = [];

  const conditionals: Array<{ fnId: ConditionalFunctionId; args: any }> = [];

  function traverseBlock(block: AcceptedStrategyBlocks) {
    switch (block.blockType) {
      case "asset":
        if (!isTokenAssetStrategyBlock(block) || block.assetId === "") {
          // not set, don't add...
        } else {
          const defiToken = block.defiToken?.assetId;
          assets.push({
            assetId: block.assetId,
            defiTokens: defiToken ? [{ assetId: defiToken }] : [],
          });
        }
        break;
      case "conditional":
        // Special case: conditional block can have assetIds in comparisonData
        if (block.comparisonData.evalDataLeft?.args) {
          if (
            block.comparisonData.evalDataLeft?.args?.assetId &&
            !!block.comparisonData.evalDataLeft.args?.assetId &&
            (block.comparisonData.evalDataLeft?.args as any)?.assetId !== "" // KLUDGE(johnrjj) - I want to ensure
          ) {
            invariant(
              block.comparisonData.evalDataLeft.args.assetId,
              "assetId is undefined - do not traverse. bad logic in gate.",
            );
            assets.push({
              assetId: block.comparisonData.evalDataLeft.args.assetId,
            });
          }
        }
        if (block.comparisonData.evalDataRight?.args) {
          // KLUDGE(johnrjj) - This is a hack to get around the type strictness. Need to type defaults for a fn args (a bunch of empty strings)
          if (
            (block.comparisonData.evalDataRight?.args as HasAssetArg)
              ?.assetId && // Just using CurrentPriceFnArgs here for now. Need to type this better
            // KLUDGE(johnrjj) - This is a hack to get around the type strictness. Right now empty string is not a typed value.
            // We need to check that the assetId is not empty string (if empty string, then it's not set)
            (block.comparisonData.evalDataRight?.args as HasAssetArg as any)
              ?.assetId !== ""
          ) {
            if (
              (block.comparisonData.evalDataRight.args as HasAssetArg).assetId
            ) {
              assets.push({
                assetId: (
                  block.comparisonData.evalDataRight.args as HasAssetArg
                ).assetId!,
              });
            }
          }
        }
        block.children.forEach(traverseBlock);
        break;
      case "filter":
        block.children.forEach(traverseBlock);
        break;
      case "dynamic":
        block.children.forEach(traverseBlock);
        break;
      case "weight":
        block.children.forEach(traverseBlock);
        break;
      case "group":
        traverseBlock(block.children);
        break;
      case "topLevel":
        // Legacy blueprint drafts can persist a root-only top-level block
        // before an allocation child is added. It is a known empty strategy,
        // not an unreadable/unsupported tree.
        if (block.children) {
          traverseBlock(block.children);
        }
        break;
      case "placeholder":
        // leaf node... noop and return
        break;
      default:
        throw new Error(`Block type ${block.blockType} not supported`);
    }
  }

  traverseBlock(block);

  return {
    assets: assets,
    baseAssets: uniq(assets.map((x) => x.assetId)),
    defiAssets: uniq(
      assets.flatMap((x) => x.defiTokens ?? []).map((x) => x.assetId),
    ),
  };
}

export type { VmFamily };

/**
 * Returns the set of VM families (`evm` / `svm` / `sui`) referenced by every
 * asset in a strategy (base assets and defi tokens). Built on top of
 * {@link getAllAssetIdsInvolvedInStrategy}, so it surfaces assets at any depth.
 *
 * @throws if the strategy tree contains an unknown block type.
 */
export function getStrategyVmFamilies(
  strategy: Pick<Strategy, "entry">,
): Set<VmFamily> {
  const { baseAssets, defiAssets } = getAllAssetIdsInvolvedInStrategy(
    strategy.entry,
  );
  return deriveVmFamiliesFromAssetIds([...baseAssets, ...defiAssets]);
}

/**
 * True when a strategy mixes VM families or references a VM that portfolio
 * construction does not support yet. A portfolio is rebalanced as a single
 * unit on one virtual machine, so all of its assets must belong to the same
 * supported VM family.
 *
 * This is the single source of truth for the "no mixed/unsupported VM
 * portfolio" invariant; every write path (strategy validation, blueprint
 * update, and portfolio creation) should funnel through it. A strategy tree we
 * cannot read fails closed.
 */
export function strategyMixesVmFamilies(
  strategy: Pick<Strategy, "entry"> | null | undefined,
): boolean {
  if (!strategy?.entry) return false;
  try {
    const families = getStrategyVmFamilies(strategy);
    return families.size > 1 || families.has("sui");
  } catch {
    return true;
  }
}

/**
 * Represents a usage of a conditional function within a strategy.
 */
export interface ConditionalFunctionUsage {
  fnId: ConditionalFunctionId;
  args: CondLeftArgsNoRuntime | CondRightArgsNoRuntime;
  blockId: string;
  parentBlockId: string;
  side: "left" | "right";
}

/**
 * Traverses the strategy and collects all unique ConditionalFunctionUsages used in conditional blocks.
 * @param strategy The strategy to traverse.
 * @returns An array of ConditionalFunctionUsage objects used in the strategy.
 */
export function getAllConditionalFunctionUsagesInStrategy(
  strategy: Strategy,
): ConditionalFunctionUsage[] {
  const functionUsages: ConditionalFunctionUsage[] = [];

  /**
   * Recursively traverses each block in the strategy.
   * @param block The current strategy block being traversed.
   */
  function traverseBlock(block: AcceptedStrategyBlocks) {
    if (isConditionalStrategyBlock(block)) {
      const { comparisonData } = block;
      const { evalDataLeft, evalDataRight } = comparisonData;

      // Extract function usage from evalDataLeft if it exists and is valid
      if (
        evalDataLeft?.id &&
        isValidConditionalFunctionId(evalDataLeft.id) &&
        evalDataLeft.args
      ) {
        functionUsages.push({
          args: evalDataLeft as CondLeftArgsNoRuntime,
          blockId: block.blockIdForInstance,
          fnId: evalDataLeft.id as ConditionalFunctionId,
          parentBlockId: block.parentNodeId,
          side: "left",
        });
      }

      // Extract function usage from evalDataRight if it exists and is valid
      if (
        evalDataRight?.id &&
        isValidConditionalFunctionId(evalDataRight.id) &&
        evalDataRight.args
      ) {
        functionUsages.push({
          args: evalDataRight as CondRightArgsNoRuntime,
          blockId: block.blockIdForInstance,
          fnId: evalDataRight.id as ConditionalFunctionId,
          parentBlockId: block.parentNodeId,
          side: "right",
        });
      }

      // Traverse child blocks
      block.children.forEach(traverseBlock);
    } else {
      // For other block types that can have children, traverse them
      switch (block.blockType) {
        case "topLevel":
        case "group":
        case "weight":
        case "filter":
          if (block.children) {
            if (Array.isArray(block.children)) {
              block.children.forEach(traverseBlock);
            } else {
              traverseBlock(block.children);
            }
          }
          break;
        // Asset, Placeholder, ActionButton blocks are leaf nodes; no action needed
        case "asset":
        case "placeholder":
        case "action-button":
          // No conditional functions to extract
          break;
        default:
          throw new Error(
            `Unsupported block type: ${(block as any).blockType}`,
          );
      }
    }
  }

  /**
   * Validates if the provided function ID is a valid ConditionalFunctionId.
   * @param id The function ID to validate.
   * @returns True if valid, otherwise false.
   */
  function isValidConditionalFunctionId(
    id: string,
  ): id is ConditionalFunctionId {
    // Assuming AVAILABLE_CONDITIONAL_FUNCTION_IDS is imported or accessible here
    return AVAILABLE_CONDITIONAL_FUNCTION_IDS.includes(
      id as ConditionalFunctionId,
    );
  }

  // Start traversal from the entry block of the strategy
  traverseBlock(strategy.entry);

  // Remove duplicate function usages based on fnId and args
  const uniqueFunctionUsages = uniqBy(functionUsages, (fu) =>
    hash({ args: fu.args, fnId: fu.fnId }),
  );

  return uniqueFunctionUsages;
}

/**
 * Traverses the strategy and collects all unique ConditionalFunctionIds used in conditional blocks.
 * @param strategy The strategy to traverse.
 * @returns An array of unique ConditionalFunctionIds used in the strategy.
 */
export function getAllConditionalFunctionIdsInStrategy(
  strategy: Strategy,
): ConditionalFunctionId[] {
  const functionIds: ConditionalFunctionId[] = [];

  /**
   * Recursively traverses each block in the strategy.
   * @param block The current strategy block being traversed.
   */
  function traverseBlock(block: AcceptedStrategyBlocks) {
    if (isConditionalStrategyBlock(block)) {
      const { comparisonData } = block;
      const { evalDataLeft, evalDataRight } = comparisonData;

      // Extract function ID from evalDataLeft if it exists and is valid
      if (evalDataLeft?.id && isValidConditionalFunctionId(evalDataLeft.id)) {
        functionIds.push(evalDataLeft.id as ConditionalFunctionId);
      }

      // Extract function ID from evalDataRight if it exists and is valid
      if (evalDataRight?.id && isValidConditionalFunctionId(evalDataRight.id)) {
        functionIds.push(evalDataRight.id as ConditionalFunctionId);
      }

      // Traverse child blocks
      block.children.forEach(traverseBlock);
    } else {
      // For other block types that can have children, traverse them
      switch (block.blockType) {
        case "topLevel":
        case "group":
        case "weight":
        case "filter":
          if (block.children) {
            if (Array.isArray(block.children)) {
              block.children.forEach(traverseBlock);
            } else {
              traverseBlock(block.children);
            }
          }
          break;
        // Asset, Placeholder, ActionButton blocks are leaf nodes; no action needed
        case "asset":
        case "placeholder":
        case "action-button":
          // No conditional functions to extract
          break;
        default:
          throw new Error(
            `Unsupported block type: ${(block as any).blockType}`,
          );
      }
    }
  }

  /**
   * Validates if the provided function ID is a valid ConditionalFunctionId.
   * @param id The function ID to validate.
   * @returns True if valid, otherwise false.
   */
  function isValidConditionalFunctionId(
    id: string,
  ): id is ConditionalFunctionId {
    // Assuming AVAILABLE_CONDITIONAL_FUNCTION_IDS is imported or accessible here
    return AVAILABLE_CONDITIONAL_FUNCTION_IDS.includes(
      id as ConditionalFunctionId,
    );
  }

  // Start traversal from the entry block of the strategy
  traverseBlock(strategy.entry);

  // Return unique function IDs
  return uniq(functionIds);
}

// Define the input validation schema using Zod
export const InputDataForSolverSchema = z.object({
  // isAssetIdsFormat works for both EVM (0x...:chainId) and Solana (base58:chainId) formats
  // since EthOrSolanaAssetIdFormat is an alias for AssetIdFormat
  assets: z
    .array(z.string())
    .refine(isAssetIdsFormat, {
      message: "Invalid asset id format",
    }),
  initial_quantities_usd: z.array(z.string()),
  swap_costs: z.record(z.string(), z.string()),
  target_distribution_usd: z.array(z.string()),
  usd_values: z.record(z.string(), z.string()),
});

export type InputDataForSolverSchemaZod = z.infer<
  typeof InputDataForSolverSchema
>;

export type InputDataForSolverIsh = InputDataForSolverSchemaZod;

export const validateSolverInputData = (
  data: InputDataForSolverIsh,
): InputDataForSolver => {
  const result = InputDataForSolverSchema.safeParse(data);

  if (!result.success) {
    throw new Error(`Validation failed: ${result.error.message}`);
  }

  const validatedData = result.data;

  // Check if assets array length matches initial_quantities_usd and target_distribution_usd
  if (
    validatedData.assets.length !==
      validatedData.initial_quantities_usd.length ||
    validatedData.assets.length !== validatedData.target_distribution_usd.length
  ) {
    throw new Error(
      "Assets array length must match initial_quantities_usd and target_distribution_usd",
    );
  }

  // Check if swap_costs has entries for all combinations of assets
  const assetCombinations = validatedData.assets.flatMap((asset1, i) =>
    validatedData.assets.slice(i + 1).map((asset2) => [asset1, asset2]),
  );

  for (const [asset1, asset2] of assetCombinations) {
    const swapCostKey1 = `${asset1}_${asset2}`;
    const swapCostKey2 = `${asset2}_${asset1}`;

    if (
      !(swapCostKey1 in validatedData.swap_costs) ||
      !(swapCostKey2 in validatedData.swap_costs)
    ) {
      throw new Error(`Missing swap cost for assets: ${asset1} and ${asset2}`);
    }
  }

  // Check if usd_values has all assets in the record
  for (const asset of validatedData.assets) {
    if (!(asset in validatedData.usd_values)) {
      throw new Error(`Missing USD value for asset: ${asset}`);
    }
  }

  return validatedData as InputDataForSolver;
};

/**
 * Given a strategy, finds the block with the given blockId
 * @param strategy
 * @param blockId
 * @returns
 */
export function findBlockById(
  strategy: Strategy,
  blockId: string,
): AcceptedStrategyBlocks | undefined {
  function searchBlock(
    block: AcceptedStrategyBlocks,
  ): AcceptedStrategyBlocks | undefined {
    if (block.blockIdForInstance === blockId) {
      return block;
    }

    if (Array.isArray(block.children)) {
      for (const child of block.children) {
        const result = searchBlock(child);
        if (result) return result;
      }
    } else if (block.children) {
      return searchBlock(block.children);
    }

    return undefined;
  }

  return searchBlock(strategy.entry);
}

const hashStrategy = (strategy: Strategy) => {
  const strategyHash = hash(strategy.entry, { unorderedArrays: true });
  return strategyHash;
};

export const getStrategyHash = (strategy: Strategy) => {
  return hashStrategy(strategy);
};

// Hash only the strategy required assets to avoid re-computing the same data for the same strategy
// We don't really care about the strategy itself, just the assets it uses
export const getStrategyHashForAssetDataCache = (strategy: Strategy) => {
  const assets = getAllAssetIdsInvolvedInStrategy(strategy.entry);
  const sortedAssets = sortBy(assets.baseAssets, (x) => x.toString());
  const strategyHash = hash(sortedAssets, { unorderedArrays: true });
  return strategyHash;
};
